/**
 * Dashboard Hooks
 *
 * Custom hooks for analytics dashboard data fetching with auto-refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import dashboardApi from '../services/dashboardApi';
import type {
  AnalyticsOverview,
  FunnelSummary,
  TrendsResponse,
  ContentPerformance,
  RevenueMetrics,
  PlatformBreakdown,
  RealtimeData,
  StreamEvent,
  AnalyticsPeriod,
} from '../types/analytics';

// --- useDashboard: Overview + Trends + Funnel ---

interface DashboardState {
  overview: AnalyticsOverview | null;
  trends: TrendsResponse | null;
  funnel: FunnelSummary | null;
  loading: boolean;
  error: string | null;
}

export function useDashboard(period: AnalyticsPeriod = '7d') {
  const [state, setState] = useState<DashboardState>({
    overview: null,
    trends: null,
    funnel: null,
    loading: true,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      // `getFunnel` resolves to null for a non-admin rather than throwing.
      // Before it was nullable, a merchant's 403 on the admin-only funnel
      // rejected this Promise.all and wiped out the overview and trends
      // alongside it - the whole dashboard failed on one restricted panel.
      const [overview, trends, funnel] = await Promise.all([
        dashboardApi.getOverview(period),
        dashboardApi.getTrends(period),
        dashboardApi.getFunnel(period),
      ]);
      setState({ overview, trends, funnel, loading: false, error: null });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load dashboard',
      }));
    }
  }, [period]);

  useEffect(() => {
    fetchData();

    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return { ...state, refresh: fetchData };
}

// --- useRealtimeDashboard: SSE connection ---

export function useRealtimeDashboard() {
  const [realtime, setRealtime] = useState<RealtimeData | null>(null);
  const [streamEvents, setStreamEvents] = useState<StreamEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch initial realtime snapshot
  const fetchRealtime = useCallback(async () => {
    try {
      const data = await dashboardApi.getRealtime();
      setRealtime(data);
    } catch {
      // Silently fail — real-time is best-effort
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRealtime();

    // Connect SSE stream
    const disconnect = dashboardApi.connectRealtime((event) => {
      if (event.type === 'heartbeat') return;
      setStreamEvents((prev) => [event, ...prev].slice(0, 50));
    });

    // Refresh realtime snapshot every 30s
    const interval = setInterval(fetchRealtime, 30_000);

    return () => {
      disconnect();
      clearInterval(interval);
    };
  }, [fetchRealtime]);

  return { realtime, streamEvents, loading, refresh: fetchRealtime };
}

// --- useRevenueDashboard ---

export function useRevenueDashboard() {
  const [revenue, setRevenue] = useState<RevenueMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRevenue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dashboardApi.getRevenue();
      setRevenue(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load revenue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  return { revenue, loading, error, refresh: fetchRevenue };
}

// --- useContentPerformance ---

export function useContentPerformance(period: AnalyticsPeriod = '30d') {
  const [content, setContent] = useState<ContentPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dashboardApi.getContentPerformance(period);
      setContent(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return { content, loading, error, refresh: fetchContent };
}

// --- usePlatformBreakdown ---

export function usePlatformBreakdown(period: AnalyticsPeriod = '7d') {
  const [platforms, setPlatforms] = useState<PlatformBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlatforms = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dashboardApi.getPlatformBreakdown(period);
      setPlatforms(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load platform data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  return { platforms, loading, error, refresh: fetchPlatforms };
}
